// Vehicle state as shown on the live map, the vehicle list and the popup card (interface
// refresh 5C: colour plus shape, so it reads without colour too). Copied in web/ and crm/.
//   moving  – arrow, pointing the way the vehicle is heading
//   idling  – circle with a pause sign (engine on, standing still)
//   parked  – rounded square with a P
//   nodata  – hollow circle (nothing heard lately)
//   nopower – triangle with ! (tracker without supply voltage)
import type { Vehicle } from '../api';

export type MapState = 'moving' | 'idling' | 'parked' | 'nodata' | 'nopower';

export const STATE_LABEL: Record<MapState, string> = {
  moving: 'Moving', idling: 'Idling', parked: 'Parked', nodata: 'No data', nopower: 'No power',
};
export const STATE_ORDER: MapState[] = ['moving', 'idling', 'parked', 'nopower', 'nodata'];

// Trailers often run off the sidelights, so a dead supply means nothing there.
export function mapState(v: Pick<Vehicle, 'status' | 'type' | 'position'>): MapState {
  const power = v.position?.powerV;
  if (power != null && power < 3 && v.type !== 'trailer' && v.status !== 'driving') return 'nopower';
  if (v.status === 'driving') return 'moving';
  if (v.status === 'idling') return 'idling';
  if (v.status === 'stopped') return 'parked';
  return 'nodata';
}

// The shape as SVG markup (24 × 24), coloured by CSS through currentColor.
export function shapeSvg(state: MapState, course: number | null = null): string {
  const o = 'stroke="var(--marker-outline)" stroke-width="1.6" stroke-linejoin="round"';
  const g = 'var(--marker-glyph)';
  let body: string;
  switch (state) {
    case 'moving':
      body = `<path d="M12 2.6 19.6 20.2 12 16 4.4 20.2Z" fill="currentColor" ${o} transform="rotate(${course ?? 0} 12 12)"/>`;
      break;
    case 'idling':
      body = `<circle cx="12" cy="12" r="9" fill="currentColor" ${o}/><path d="M9.6 8.4v7.2M14.4 8.4v7.2" stroke="${g}" stroke-width="2.2" stroke-linecap="round"/>`;
      break;
    case 'parked':
      body = `<rect x="3.5" y="3.5" width="17" height="17" rx="4" fill="currentColor" ${o}/><path d="M10 16.6V7.6h2.7a2.6 2.6 0 0 1 0 5.2H10" fill="none" stroke="${g}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;
      break;
    case 'nopower':
      body = `<path d="M12 2.8 21.4 19.6H2.6Z" fill="currentColor" ${o}/><path d="M12 9v5" stroke="${g}" stroke-width="2.2" stroke-linecap="round"/><circle cx="12" cy="16.9" r="1.2" fill="${g}"/>`;
      break;
    default:
      body = `<circle cx="12" cy="12" r="7.5" fill="var(--marker-outline)" stroke="currentColor" stroke-width="3.2"/>`;
  }
  return `<svg class="state-shape" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">${body}</svg>`;
}

export function StateIcon({ state, course = null, size = 20 }: { state: MapState; course?: number | null; size?: number }) {
  return <span className={`state-icon st-${state}`} style={{ width: size, height: size }} dangerouslySetInnerHTML={{ __html: shapeSvg(state, course) }} />;
}

// The state as a word with its shape (status words use the text-safe colours).
export function StateWord({ state }: { state: MapState }) {
  return <span className={`state-word st-${state}`}><StateIcon state={state} size={14} />{STATE_LABEL[state]}</span>;
}
